import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
	addAgencyTaskMessageApi,
	cancelAgencyTaskApi,
	createAgencyTaskApi,
	deleteAgencyTaskApi,
	deleteAgencyTaskAssigneeApi,
	getAgencyTaskApi,
	listAgencyTaskAssigneesApi,
	listAgencyTasksApi,
	updateAgencyTaskApi,
} from "./tasksAPI";

const initialState = {
	items: [],
	nextCursor: null,
	filters: {
		status: "",
		assignee: "",
		sort: "asc",
		limit: 10,
	},
	selectedTask: null,
	assignees: [],
	listStatus: "idle",
	detailStatus: "idle",
	mutationStatus: "idle",
	assigneesStatus: "idle",
	error: null,
};

function errorMessage(error) {
	return error?.message || "Unexpected error";
}

function upsertTask(items, task) {
	if (!task) return items;
	const index = items.findIndex((item) => item.id === task.id);
	if (index === -1) return items;
	const next = [...items];
	next[index] = task;
	return next;
}

export const listAgencyTasksAsync = createAsyncThunk("tasks/list", async (params, { getState, rejectWithValue }) => {
	try {
		const stateFilters = getState().tasks.filters;
		const query = {
			...stateFilters,
			...params,
		};
		Object.keys(query).forEach((key) => {
			if (query[key] === "" || query[key] == null) delete query[key];
		});
		return await listAgencyTasksApi(query);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const getAgencyTaskAsync = createAsyncThunk("tasks/get", async (taskId, { rejectWithValue }) => {
	try {
		return await getAgencyTaskApi(taskId);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const createAgencyTaskAsync = createAsyncThunk("tasks/create", async (request, { rejectWithValue }) => {
	try {
		return await createAgencyTaskApi(request);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const updateAgencyTaskAsync = createAsyncThunk("tasks/update", async ({ taskId, ...request }, { rejectWithValue }) => {
	try {
		return await updateAgencyTaskApi(taskId, request);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const cancelAgencyTaskAsync = createAsyncThunk("tasks/cancel", async (taskId, { rejectWithValue }) => {
	try {
		return await cancelAgencyTaskApi(taskId);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const deleteAgencyTaskAsync = createAsyncThunk("tasks/delete", async (taskId, { rejectWithValue }) => {
	try {
		return await deleteAgencyTaskApi(taskId);
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const addAgencyTaskMessageAsync = createAsyncThunk(
	"tasks/addMessage",
	async ({ taskId, text }, { rejectWithValue }) => {
		try {
			return await addAgencyTaskMessageApi(taskId, { text });
		} catch (error) {
			return rejectWithValue(errorMessage(error));
		}
	}
);

export const listAgencyTaskAssigneesAsync = createAsyncThunk("tasks/assignees", async (_, { rejectWithValue }) => {
	try {
		return await listAgencyTaskAssigneesApi();
	} catch (error) {
		return rejectWithValue(errorMessage(error));
	}
});

export const deleteAgencyTaskAssigneeAsync = createAsyncThunk(
	"tasks/deleteAssignee",
	async (normalizedName, { rejectWithValue }) => {
		try {
			return await deleteAgencyTaskAssigneeApi(normalizedName);
		} catch (error) {
			return rejectWithValue(errorMessage(error));
		}
	}
);

export const tasksSlice = createSlice({
	name: "tasks",
	initialState,
	reducers: {
		setTaskFilters(state, action) {
			state.filters = { ...state.filters, ...action.payload };
		},
		clearSelectedTask(state) {
			state.selectedTask = null;
			state.detailStatus = "idle";
		},
		clearTasksError(state) {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(listAgencyTasksAsync.pending, (state) => {
				state.listStatus = "loading";
				state.error = null;
			})
			.addCase(listAgencyTasksAsync.fulfilled, (state, action) => {
				state.listStatus = "idle";
				state.items = action.payload?.tasks || [];
				state.nextCursor = action.payload?.nextCursor || null;
				if (action.meta.arg?.sort) state.filters.sort = action.meta.arg.sort;
			})
			.addCase(listAgencyTasksAsync.rejected, (state, action) => {
				state.listStatus = "idle";
				state.error = action.payload || "Failed to load tasks";
			})
			.addCase(getAgencyTaskAsync.pending, (state) => {
				state.detailStatus = "loading";
				state.error = null;
			})
			.addCase(getAgencyTaskAsync.fulfilled, (state, action) => {
				state.detailStatus = "idle";
				state.selectedTask = action.payload?.task || null;
				state.items = upsertTask(state.items, action.payload?.task);
			})
			.addCase(getAgencyTaskAsync.rejected, (state, action) => {
				state.detailStatus = "idle";
				state.error = action.payload || "Failed to load task";
			})
			.addCase(createAgencyTaskAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(createAgencyTaskAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				state.selectedTask = action.payload?.task || null;
			})
			.addCase(createAgencyTaskAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload || "Failed to create task";
			})
			.addCase(updateAgencyTaskAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(updateAgencyTaskAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				state.selectedTask = action.payload?.task || null;
				state.items = upsertTask(state.items, action.payload?.task);
			})
			.addCase(updateAgencyTaskAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload || "Failed to update task";
			})
			.addCase(cancelAgencyTaskAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(cancelAgencyTaskAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				state.selectedTask = action.payload?.task || null;
				state.items = upsertTask(state.items, action.payload?.task);
			})
			.addCase(cancelAgencyTaskAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload || "Failed to cancel task";
			})
			.addCase(deleteAgencyTaskAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(deleteAgencyTaskAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				const taskId = action.payload?.taskId || action.meta.arg;
				state.items = state.items.filter((item) => item.id !== taskId);
				if (state.selectedTask?.id === taskId) state.selectedTask = null;
			})
			.addCase(deleteAgencyTaskAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload || "Failed to delete task";
			})
			.addCase(addAgencyTaskMessageAsync.pending, (state) => {
				state.mutationStatus = "loading";
				state.error = null;
			})
			.addCase(addAgencyTaskMessageAsync.fulfilled, (state, action) => {
				state.mutationStatus = "idle";
				state.selectedTask = action.payload?.task || null;
				state.items = upsertTask(state.items, action.payload?.task);
			})
			.addCase(addAgencyTaskMessageAsync.rejected, (state, action) => {
				state.mutationStatus = "idle";
				state.error = action.payload || "Failed to add message";
			})
			.addCase(listAgencyTaskAssigneesAsync.pending, (state) => {
				state.assigneesStatus = "loading";
			})
			.addCase(listAgencyTaskAssigneesAsync.fulfilled, (state, action) => {
				state.assigneesStatus = "idle";
				state.assignees = action.payload?.assignees || [];
			})
			.addCase(listAgencyTaskAssigneesAsync.rejected, (state, action) => {
				state.assigneesStatus = "idle";
				state.error = action.payload || "Failed to load assignees";
			})
			.addCase(deleteAgencyTaskAssigneeAsync.fulfilled, (state, action) => {
				const normalizedName = action.payload?.normalizedName || action.meta.arg;
				state.assignees = state.assignees.filter((entry) => entry.normalizedName !== normalizedName);
			})
			.addCase(deleteAgencyTaskAssigneeAsync.rejected, (state, action) => {
				state.error = action.payload || "Failed to delete assignee suggestion";
			});
	},
});

export const { setTaskFilters, clearSelectedTask, clearTasksError } = tasksSlice.actions;
export const selectTasks = (state) => state.tasks.items;
export const selectSelectedTask = (state) => state.tasks.selectedTask;
export const selectTaskAssignees = (state) => state.tasks.assignees;
export default tasksSlice.reducer;
